import axios from 'axios';

const API_ROOT = 'https://louca.certifia.ai';
const API_BASE = `${API_ROOT}/`;
const TOKEN = 'louca-certifia-2025';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Authorization': `Bearer ${TOKEN}`,
    'Content-Type': 'application/json',
  },
});

const withBaseUrl = (relativePath = '') => {
  if (!relativePath) return null;
  if (relativePath.startsWith('http')) return relativePath;
  let path = relativePath;
  
  // Remove /louca prefix if already present (legacy URLs from old backend)
  // This prevents double prefix: /louca/louca/api/v0.2/...
  path = path.replace(/^\/louca\//, '/');
  
  // Normalize legacy filesystem paths to API uploads endpoint
  // /app/uploads/{iid}/...  -> /api/v0.2/uploads/{iid}/...
  path = path.replace(/^\/louca\/app\/uploads\//, '/api/v0.2/uploads/');
  path = path.replace(/^\/app\/uploads\//, '/api/v0.2/uploads/');
  
  // Ensure leading slash is handled under API_ROOT
  if (path.startsWith('/')) {
    return `${API_ROOT}${path}`;
  }
  return `${API_ROOT}/${path}`;
};

const withToken = (url) => {
  if (!url) return null;
  if (url.includes('token=')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${TOKEN}`;
};

export const buildImageUrl = (relativePath) => withToken(withBaseUrl(relativePath));

export const loucaApi = {
  /**
   * Login mock - kept for compatibility
   */
  login: async (user_id) => {
    const response = await api.post('/auth/login', { user_id });
    return response.data;
  },

  /**
   * Create new inspection with video upload (legacy workflow)
   */
  createInspection: async (formData) => {
    const response = await api.post('/inspection_ini', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Create new inspection using the simple prompt workflow
   */
  createInspectionSimple: async (formData) => {
    const response = await api.post('/inspection_ini_v2', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Send a plain text prompt to Gemini to validate availability
   */
  testPrompt: async (payload) => {
    const response = await api.post('/llm/test_prompt', payload);
    return response.data;
  },

  /**
   * Retrieve registered AI engines
   */
  getAIEngines: async () => {
    const response = await api.get('/ai/engines');
    return response.data;
  },

  /**
   * Launch YOLO exterior analysis (video + version)
   */
  analyzeExterior: async (formData) => {
    const response = await api.post('/api/v0.2/analyze/exterior', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get inspection status (for polling)
   */
  getInspectionStatus: async (inspection_id) => {
    const response = await api.get(`/inspection_status/${inspection_id}`);
    return response.data;
  },

  /**
   * Get full inspection details
   */
  getInspection: async (inspection_id) => {
    const response = await api.get(`/inspections/${inspection_id}`);
    return response.data;
  },

  /**
   * Update a field in the inspection report (inline editing)
   */
  updateLine: async (data) => {
    const response = await api.patch('/inspection_update_line', data);
    return response.data;
  },

  /**
   * Get list of inspections for an owner
   */
  getInspectedCars: async (owner_id) => {
    const response = await api.get(`/inspected_cars?owner_id=${owner_id}`);
    return response.data;
  },

  /**
   * Compare two inspections
   */
  compare: async (data) => {
    const response = await api.post('/inspection_compare', data);
    return response.data;
  },

  /**
   * Test image analysis with different workflows
   */
  testImage: async (imageFile, workflow, userId, owner, yoloParams = {}) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('workflow', workflow);
    formData.append('user_id', userId);
    formData.append('owner', typeof owner === 'string' ? owner : JSON.stringify(owner));
    
    // Add YOLO hyperparameters if provided
    if (yoloParams.confidence_thresh !== undefined) {
      formData.append('confidence_thresh', yoloParams.confidence_thresh);
    }
    if (yoloParams.iou_threshold !== undefined) {
      formData.append('iou_threshold', yoloParams.iou_threshold);
    }
    if (yoloParams.max_det !== undefined) {
      formData.append('max_det', yoloParams.max_det);
    }
    if (yoloParams.batch_size !== undefined) {
      formData.append('batch_size', yoloParams.batch_size);
    }
    if (yoloParams.imgsz !== undefined) {
      formData.append('imgsz', yoloParams.imgsz);
    }
    
    const response = await api.post('/api/v0.2/test/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Launch V2.1 Vehicle Analysis (Strategy Pattern)
   */
  analyzeVehicleV2: async (formData) => {
    const response = await api.post('/api/v2.1/analyze/vehicle', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get list of V2.1 inspections
   */
  listInspectionsV2: async () => {
    const response = await api.get('/api/v2.1/inspections');
    return response.data;
  },

  /**
   * Get details of a V2.1 inspection
   */
  getInspectionV2: async (id) => {
    const response = await api.get(`/api/v2.1/inspections/${id}`);
    return response.data;
  },

  // ========== V9 API Methods ==========

  /**
   * Get V3 configuration (compatibility endpoint)
   */
  getConfigV3: async () => {
    const response = await api.get('/api/v3/config');
    return response.data;
  },

  /**
   * Get V9 configuration
   */
  getConfigV9: async () => {
    const response = await api.get('/api/v9/config');
    return response.data;
  },

  /**
   * Get available LLM models
   */
  getLLMModels: async () => {
    const response = await api.get('/api/v9/settings/llm-models');
    return response.data;
  },

  /**
   * Create a new V9 session
   */
  createSessionV3: async (config = {}) => {
    const response = await api.post('/api/v9/sessions', config);
    return response.data;
  },

  /**
   * List all V9 sessions
   */
  listSessionsV3: async () => {
    const response = await api.get('/api/v9/sessions');
    return response.data;
  },

  /**
   * Get a specific V9 session
   */
  getSessionV3: async (sessionId) => {
    const response = await api.get(`/api/v9/sessions/${sessionId}`);
    return response.data;
  },

  /**
   * Upload a video chunk to a session
   */
  uploadChunkV3: async (sessionId, chunkFile, chunkIndex = 0) => {
    const formData = new FormData();
    formData.append('chunk', chunkFile);
    formData.append('chunk_index', chunkIndex);
    
    const response = await api.post(`/api/v9/sessions/${sessionId}/chunks`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Get results for a session
   */
  getSessionResultsV3: async (sessionId) => {
    const response = await api.get(`/api/v9/sessions/${sessionId}/results`);
    return response.data;
  },
};

export default loucaApi;



